import React, { useState, useEffect } from "react";
import {
  CheckCircle2,
  Users,
  CalendarDays,
  ChevronRight,
  Loader2,
  UserCheck,
  History,
  LayoutDashboard,
  MapPin,
  Clock,
  Sparkles,
  CircleHelp,
  School,
  Building2,
  BarChart3,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AttendanceSheetModal from "./AttendanceSheetModal";
import AttendanceHistory from "./AttendanceHistory";
import AttendanceReport from "./AttendanceReport";
import AttendanceDailyFilter from "./AttendanceDailyFilter";
import { getAttendanceClasses } from "../../../api/elearning/attendance/attendance";
import { showErrorToast } from "../../../components/ConfirmDialog";
import { todayLocalISODate } from "../../../utils/formatUtils";
import { hasPermission } from "../../../utils/permissionUtils";
import { startAttendancePageTour } from "./attendancePageTour";

import { getClassTimeListParts } from "../utils/classTimeDisplay";

const VenueBadge = ({ venue, label }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide border ${
      venue === "school"
        ? "bg-amber-50 text-amber-800 border-amber-200"
        : "bg-slate-50 text-slate-600 border-slate-200"
    }`}
  >
    {label}
  </span>
);

const StatusBadge = ({ status }) => {
  const done = status === "completed";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide border ${
        done
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-orange-50 text-orange-700 border-orange-200"
      }`}
    >
      {done ? <CheckCircle2 size={11} /> : <Clock size={11} />}
      {done ? "Đã xong" : "Chờ điểm danh"}
    </span>
  );
};

const StatCard = ({ label, value, icon: Icon, iconWrap }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3.5 flex items-center gap-3">
    <div className={`p-2.5 rounded-lg ${iconWrap}`}>
      <Icon size={18} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate">{label}</p>
      <p className="text-lg font-black text-gray-900 leading-tight">{value}</p>
    </div>
  </div>
);

const AttendancePage = () => {
  const [activeTab, setActiveTab] = useState("daily");
  const [classList, setClassList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedDate, setSelectedDate] = useState(() => todayLocalISODate());
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [venueScope, setVenueScope] = useState("center");
  const [branchFilter, setBranchFilter] = useState("all");
  const [attendanceMeta, setAttendanceMeta] = useState({
    can_filter_all_branches: false,
    branches: [],
  });
  const canQuickTour = hasPermission("elearning.attendance", "R");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchClasses = async () => {
      setLoading(true);
      try {
        const { records, meta } = await getAttendanceClasses(selectedDate, {
          venue: venueScope,
          branch_id: branchFilter,
        });
        const canHq = Boolean(meta?.can_filter_all_branches);
        setAttendanceMeta({
          can_filter_all_branches: canHq,
          branches: Array.isArray(meta?.branches) ? meta.branches : [],
        });

        setClassList(
          records.map((r) => {
            const timeParts = getClassTimeListParts(r.class_time);
            return {
            id: r.id,
            name: r.name,
            student_count: r.student_count ?? 0,
            schedule: timeParts.schedule,
            days: timeParts.days,
            room: r.room_name || "—",
            status: r.status || "pending",
            venue: r.venue === "school" ? "school" : "center",
            venue_label: r.venue === "school" ? "Tại trường" : "Trung tâm",
            school_customer_name: r.school_customer_name || "",
            branch_name: r.branch_name || "",
          };
          })
        );
      } catch {
        showErrorToast("Không thể tải danh sách lớp");
        setClassList([]);
      } finally {
        setLoading(false);
      }
    };
    if (activeTab === "daily") fetchClasses();
  }, [activeTab, selectedDate, listRefreshKey, venueScope, branchFilter]);

  const filteredClasses = classList.filter((c) =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalClasses = classList.length;
  const completedClasses = classList.filter((c) => c.status === "completed").length;
  const pendingClasses = totalClasses - completedClasses;

  const openClassSheet = (cls) => {
    setSelectedClass(cls);
    setIsModalOpen(true);
  };

  const handleDailyRefresh = () => setListRefreshKey((k) => k + 1);
  const handleQuickTour = () => {
    window.setTimeout(() => startAttendancePageTour(), 80);
  };
  const openGuide = () => {
    const guideUrl = `${window.location.origin}/guides/elearning-attendance-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  };

  const filterProps = {
    selectedDate,
    onDateChange: setSelectedDate,
    searchTerm,
    onSearchChange: setSearchTerm,
    onRefresh: handleDailyRefresh,
    venue: venueScope,
    onVenueChange: setVenueScope,
    canFilterAllBranches: attendanceMeta.can_filter_all_branches,
    branchId: branchFilter,
    branches: attendanceMeta.branches,
    onBranchChange: setBranchFilter,
  };

  const RenderMobileDaily = () => (
    <div className="space-y-3 pb-[max(1rem,env(safe-area-inset-bottom))]" data-tour="attendance-daily-mobile">
      <AttendanceDailyFilter {...filterProps} isMobile />

      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Lớp hôm nay", value: totalClasses, tone: "text-blue-600" },
          {
            label: "Đã xong",
            value: `${completedClasses}/${totalClasses || 0}`,
            tone: "text-emerald-600",
          },
          { label: "Chờ DD", value: pendingClasses, tone: "text-orange-500" },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-white px-2 py-2.5 rounded-xl border border-gray-100 shadow-sm text-center"
          >
            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-1">
              {s.label}
            </p>
            <p className={`text-lg font-black leading-none tabular-nums ${s.tone}`}>
              {s.value}
            </p>
          </div>
        ))}
      </div>

      <div className="space-y-2" data-tour="attendance-classes">
        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="animate-spin text-red-500" size={26} />
          </div>
        )}
        {!loading && filteredClasses.length === 0 && (
          <div className="bg-white rounded-xl border border-dashed border-gray-200 py-10 px-4 text-center">
            <CalendarDays className="mx-auto text-gray-300 mb-2" size={28} />
            <p className="text-gray-500 text-sm font-medium">
              Chưa có lớp học nào trong ngày này.
            </p>
          </div>
        )}
        {!loading &&
          filteredClasses.map((cls) => (
            <button
              type="button"
              key={cls.id}
              onClick={() => openClassSheet(cls)}
              className="w-full bg-white p-3.5 rounded-xl shadow-sm border border-gray-100 text-left active:scale-[0.985] active:bg-red-50/40 transition touch-manipulation"
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                    cls.status === "completed"
                      ? "bg-emerald-50 text-emerald-600"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  <UserCheck size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-bold text-gray-900 text-[15px] leading-snug line-clamp-2">
                      {cls.name}
                    </h4>
                    <ChevronRight size={18} className="text-gray-300 shrink-0 mt-0.5" />
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <VenueBadge venue={cls.venue} label={cls.venue_label} />
                    <StatusBadge status={cls.status} />
                  </div>
                  {cls.venue === "school" && cls.school_customer_name ? (
                    <p className="text-[11px] font-medium text-amber-700 mt-1.5 truncate">
                      {cls.school_customer_name}
                    </p>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] font-semibold text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <Clock size={12} className="text-red-400" />
                      {cls.schedule || "—"}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={12} />
                      {cls.room}
                    </span>
                    <span className="inline-flex items-center gap-1 text-blue-600">
                      <Users size={12} />
                      {cls.student_count} HV
                    </span>
                  </div>
                </div>
              </div>
            </button>
          ))}
      </div>
    </div>
  );

  const RenderDesktopDaily = () => (
    <div className="space-y-4" data-tour="attendance-daily-desktop">
      <div data-tour="attendance-filter">
        <AttendanceDailyFilter {...filterProps} isMobile={false} />
      </div>

      <div className="grid grid-cols-3 gap-3" data-tour="attendance-stats">
        <StatCard
          label="Lớp học ngày chọn"
          value={`${totalClasses} lớp`}
          icon={CalendarDays}
          iconWrap="bg-blue-50 text-blue-600"
        />
        <StatCard
          label="Đã hoàn thành"
          value={`${completedClasses} lớp`}
          icon={CheckCircle2}
          iconWrap="bg-emerald-50 text-emerald-600"
        />
        <StatCard
          label="Chờ điểm danh"
          value={`${pendingClasses} lớp`}
          icon={Users}
          iconWrap="bg-orange-50 text-orange-600"
        />
      </div>

      <div
        className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
        data-tour="attendance-classes"
      >
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-gray-800">Danh sách lớp cần điểm danh</h3>
            <p className="text-[11px] text-gray-400 font-medium">
              Nhấn vào dòng để mở phiếu điểm danh
            </p>
          </div>
          <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
            {filteredClasses.length} lớp
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-50/70 border-b border-gray-100 text-left">
                <th className="p-3 w-12 text-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  #
                </th>
                <th className="p-3 min-w-[200px] text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  Lớp học
                </th>
                <th className="p-3 min-w-[140px] text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  Địa điểm
                </th>
                <th className="p-3 min-w-[140px] text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  Lịch / Phòng
                </th>
                <th className="p-3 min-w-[90px] text-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  Sĩ số
                </th>
                <th className="p-3 min-w-[120px] text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="p-3 w-14 text-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading && (
                <tr>
                  <td colSpan={7} className="py-14 text-center">
                    <Loader2 className="inline-block animate-spin text-red-500" size={28} />
                  </td>
                </tr>
              )}
              {!loading && filteredClasses.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-14 text-center text-sm text-gray-500 font-medium">
                    Chưa có lớp học nào trong ngày này.
                  </td>
                </tr>
              )}
              {!loading &&
                filteredClasses.map((cls, index) => (
                  <tr
                    key={cls.id}
                    onClick={() => openClassSheet(cls)}
                    className="hover:bg-red-50/40 transition-colors cursor-pointer group"
                  >
                    <td className="p-3 text-center text-xs text-gray-400 font-medium italic">
                      {String(index + 1).padStart(2, "0")}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-bold text-gray-900 group-hover:text-red-600 transition-colors">
                          {cls.name}
                        </span>
                        {cls.branch_name ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 font-medium">
                            <Building2 size={11} /> {cls.branch_name}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="space-y-1">
                        <VenueBadge venue={cls.venue} label={cls.venue_label} />
                        {cls.venue === "school" && cls.school_customer_name ? (
                          <p className="text-[11px] font-medium text-amber-700 flex items-center gap-1">
                            <School size={11} className="shrink-0" />
                            <span className="truncate max-w-[180px]">{cls.school_customer_name}</span>
                          </p>
                        ) : null}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="space-y-1 text-xs font-semibold text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Clock size={12} className="text-red-500 shrink-0" />
                          <span>
                            {cls.days || "—"}
                            {cls.schedule ? ` · ${cls.schedule}` : ""}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-400">
                          <MapPin size={12} className="shrink-0" />
                          <span>{cls.room}</span>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center gap-1 text-sm font-bold text-gray-700">
                        <Users size={13} className="text-blue-500" />
                        {cls.student_count}
                      </span>
                    </td>
                    <td className="p-3">
                      <StatusBadge status={cls.status} />
                    </td>
                    <td className="p-3 text-center">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gray-50 text-gray-400 border border-gray-100 group-hover:bg-red-600 group-hover:text-white group-hover:border-red-600 transition-all">
                        <ChevronRight size={16} />
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-3 md:p-4 text-left pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div
        className="flex items-center justify-between gap-3 mb-4 md:mb-6"
        data-tour="attendance-header"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="bg-red-700 p-2 md:p-2.5 rounded-xl md:rounded-2xl text-white shadow-lg shadow-red-200 shrink-0">
            <UserCheck size={isMobile ? 20 : 24} />
          </div>
          <div className="min-w-0">
            <h1 className="text-base md:text-xl font-black text-gray-800 tracking-tight uppercase leading-tight">
              Quản lý <span className="text-red-700">điểm danh</span>
            </h1>
            <p className="hidden sm:flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 font-bold uppercase tracking-wider">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Theo dõi chuyên cần và liên hệ học viên vắng
            </p>
          </div>
        </div>
        <div
          className="flex items-center gap-1.5 shrink-0"
          data-tour="attendance-header-guides"
        >
          {canQuickTour && !isMobile ? (
            <HeaderIconButton
              icon={Sparkles}
              label="Hướng dẫn nhanh"
              onClick={handleQuickTour}
            />
          ) : null}
          <HeaderIconButton
            icon={CircleHelp}
            label="Hướng dẫn sử dụng"
            onClick={openGuide}
            compact={isMobile}
          />
        </div>
      </div>

      <div
        className="mb-4 md:mb-5 sticky top-0 z-20 -mx-3 px-3 md:mx-0 md:px-0 md:static bg-white/95 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none py-1.5 md:py-0 border-b border-gray-100/80 md:border-0"
        data-tour="attendance-tabs"
      >
        <div className="inline-flex p-1 bg-gray-100 rounded-xl w-full md:w-auto">
          <button
            type="button"
            onClick={() => setActiveTab("daily")}
            className={`flex-1 md:flex-none min-h-[42px] px-2.5 md:px-4 py-2 rounded-lg text-[11px] md:text-xs font-bold transition-all flex items-center justify-center gap-1.5 touch-manipulation ${
              activeTab === "daily"
                ? "bg-white text-red-600 shadow-sm"
                : "text-gray-500"
            }`}
          >
            <LayoutDashboard size={14} />
            {isMobile ? "Hàng ngày" : "Điểm danh hàng ngày"}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("report")}
            className={`flex-1 md:flex-none min-h-[42px] px-2.5 md:px-4 py-2 rounded-lg text-[11px] md:text-xs font-bold transition-all flex items-center justify-center gap-1.5 touch-manipulation ${
              activeTab === "report"
                ? "bg-white text-red-600 shadow-sm"
                : "text-gray-500"
            }`}
          >
            <BarChart3 size={14} />
            {isMobile ? "Báo cáo" : "Báo cáo điểm danh"}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("history")}
            className={`flex-1 md:flex-none min-h-[42px] px-2.5 md:px-4 py-2 rounded-lg text-[11px] md:text-xs font-bold transition-all flex items-center justify-center gap-1.5 touch-manipulation ${
              activeTab === "history"
                ? "bg-white text-red-600 shadow-sm"
                : "text-gray-500"
            }`}
          >
            <History size={14} />
            {isMobile ? "Lịch sử" : "Lịch sử & liên hệ"}
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === "daily" ? (
          <motion.div key="daily" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {isMobile ? <RenderMobileDaily /> : <RenderDesktopDaily />}
          </motion.div>
        ) : activeTab === "history" ? (
          <motion.div key="history" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <AttendanceHistory />
          </motion.div>
        ) : (
          <motion.div key="report" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <AttendanceReport />
          </motion.div>
        )}
      </AnimatePresence>

      <AttendanceSheetModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        classInfo={selectedClass}
        date={selectedDate}
        onSaved={() => {
          setClassList((prev) =>
            prev.map((c) => (c.id === selectedClass?.id ? { ...c, status: "completed" } : c))
          );
        }}
      />
    </div>
  );
};

export default AttendancePage;

function HeaderIconButton({ icon: Icon, label, onClick, compact = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`group relative inline-flex items-center justify-center rounded-xl md:rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300 ${
        compact ? "h-10 w-10" : "h-11 w-11"
      }`}
    >
      <Icon className={compact ? "h-4.5 w-4.5" : "h-5 w-5"} size={compact ? 18 : 20} strokeWidth={2.2} />
      <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0 hidden md:block">
        {label}
      </span>
    </button>
  );
}
